import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from '@mui/material';
import {
  Store as StoreIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

import { useAuth } from '../../contexts/AuthContext';
import StatsCard from '../../components/Dashboard/StatsCard';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import BrandPlanningTab from '../../components/Brands/BrandPlanningTab';
import BrandStorefrontTab from '../../components/Brands/BrandStorefrontTab';
import OnboardingSuccessBanner from '../../components/Onboarding/OnboardingSuccessBanner';
import { fetchBrand } from '../../api/brands';
import DesignStudioPage from '../Design/DesignStudioPage';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BrandDashboardPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user, hasBrandPermission } = useAuth();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [planningTab, setPlanningTab] = useState('planner');
  
  // Dialog states
  const [campaignMenuAnchor, setCampaignMenuAnchor] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [createCampaignDialog, setCreateCampaignDialog] = useState(false);
  
  useEffect(() => {
    let mounted = true;

    const loadBrandData = async () => {
      try {
        setLoading(true);
        const brandData = await fetchBrand(brandId);
        if (!mounted) return;

        setBrand(brandData);
        setAnalytics(brandData.analytics || null);
        setCampaigns(
          (brandData.analytics?.recentCampaigns || []).map((campaign) => ({
            ...campaign,
            _id: campaign._id || campaign.id,
          }))
        );
        setRecentActivity(brandData.analytics?.recentActivity || []);
      } catch (error) {
        console.error('Error fetching brand data:', error);
        toast.error('Failed to load brand data');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (brandId) {
      loadBrandData();
    }

    return () => {
      mounted = false;
    };
  }, [brandId]);
  
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handlePlanningTabChange = (event, value) => {
    setPlanningTab(value);
  };
  
  const handleCampaignMenuOpen = (event, campaign) => {
    setCampaignMenuAnchor(event.currentTarget);
    setSelectedCampaign(campaign);
  };
  
  const handleCampaignMenuClose = () => {
    setCampaignMenuAnchor(null);
    setSelectedCampaign(null);
  };
  
  const handleLaunchCampaign = async (campaignId) => {
    try {
      // API call to launch campaign
      toast.success('Campaign launched successfully');
      handleCampaignMenuClose();
      // Refresh campaigns
    } catch (error) {
      toast.error('Failed to launch campaign');
    }
  };
  
  const handlePauseCampaign = async (campaignId) => {
    try {
      // API call to pause campaign
      toast.success('Campaign paused');
      handleCampaignMenuClose();
      // Refresh campaigns
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!brand) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5">Brand not found</Typography>
        <Button variant="contained" onClick={() => navigate('/brands')} sx={{ mt: 2 }}>
          Back to Brands
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Brand header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}
            src={brand.profile?.logo}
          >
            {brand.name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {brand.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {brand.profile?.description || 'No description available'}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateCampaignDialog(true)}
            >
              New Campaign
            </Button>
          </Box>
        </Box>
      </Box>
      
      <OnboardingSuccessBanner brandId={brandId} />

      {/* Stats overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Revenue"
            value={`$${brand.analytics?.totalRevenue?.toLocaleString() || '0'}`}
            icon={<AnalyticsIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Reservations"
            value={brand.analytics?.totalReservations?.toLocaleString() || '0'}
            icon={<CampaignIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Campaigns"
            value={brand.analytics?.activeCampaigns || '0'}
            icon={<LaunchIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Campaigns"
            value={brand.analytics?.totalCampaigns || '0'}
            icon={<StoreIcon />}
            color="secondary"
          />
        </Grid>
      </Grid>
      
      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Tabs value={currentTab >= 0 && currentTab <= 4 ? currentTab : false} onChange={handleTabChange}>
            <Tab label="Overview" value={0} />
            <Tab label="Planning" value={1} />
            <Tab label="Design" value={2} />
            <Tab label="Campaigns" value={3} />
            <Tab label="Storefront" value={4} />
          </Tabs>
          <Box sx={{ flexGrow: 1 }} />
          <Tabs value={currentTab >= 5 && currentTab <= 6 ? currentTab : false} onChange={handleTabChange}>
            <Tab label="Analytics" value={5} />
            <Tab label="Settings" value={6} />
          </Tabs>
        </Box>
        
        {/* Overview Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            {/* Active campaigns */}
            <Grid item xs={12} md={8}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Active Campaigns
              </Typography>
              
              {campaigns.filter(c => c.status === 'active').length === 0 ? (
                <Alert severity="info">
                  No active campaigns. Create your first campaign to get started.
                </Alert>
              ) : (
                <Grid container spacing={2}>
              {campaigns.filter(c => c.status === 'active').map((campaign) => {
                const launchDate = campaign.timeline?.launchDate ? new Date(campaign.timeline.launchDate) : null;
                return (
                <Grid item xs={12} key={campaign._id || campaign.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {campaign.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {launchDate ? `Launched ${formatDistanceToNow(launchDate)} ago` : 'Launch date pending'}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={campaign.status}
                          color="success"
                            />
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            {(campaign.tiers || []).map((tier, index) => (
                              <Box key={index} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2">
                                    {tier.name}: {tier.reservations}/{tier.reservationTarget}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    {tier.progress}%
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/campaigns/${campaign._id || campaign.id}`)}
                            >
                              View Details
                            </Button>
                            <Button
                              size="small"
                              onClick={() => navigate(`/campaigns/${campaign._id || campaign.id}/edit`)}
                            >
                              Edit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )})}
                </Grid>
              )}
            </Grid>
            
            {/* Quick stats */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              
              {recentActivity.length === 0 ? (
                <Alert severity="info">
                  No recent activity recorded for this brand.
                </Alert>
              ) : (
                <List>
                  {recentActivity.map((activity, index) => {
                    const timestamp = activity.timestamp ? new Date(activity.timestamp) : null;
                    const isCampaign = activity.type === 'campaign';
                    return (
                      <ListItem key={`${activity.type}-${index}`}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: isCampaign ? 'info.main' : 'success.main' }}>
                            {isCampaign ? <CampaignIcon /> : <LaunchIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={activity.title}
                          secondary={timestamp ? formatDistanceToNow(timestamp, { addSuffix: true }) : null}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Campaigns Tab */}
        <TabPanel value={currentTab} index={1}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Planning Workspace
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Move from calendar → plan → drop configuration with one workflow.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={() => setPlanningTab('dropCalendar')}
              >
                Drop Calendar
              </Button>
              {planningTab === 'campaigns' && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateCampaignDialog(true)}
                >
                  New Campaign
                </Button>
              )}
            </Box>
          </Box>

          <Tabs
            value={planningTab}
            onChange={handlePlanningTabChange}
            aria-label="Planning view tabs"
            sx={{ mb: 3 }}
          >
            <Tab label="Planner" value="planner" />
            <Tab label="Drop Calendar" value="dropCalendar" />
            <Tab label="Drop Config" value="dropConfig" />
            <Tab label="Campaign Ledger" value="campaigns" />
          </Tabs>

          {planningTab === 'planner' ? (
            <BrandPlanningTab brandId={brandId} />
          ) : planningTab === 'dropCalendar' ? (
            <Box
              sx={{
                minHeight: 720,
                height: 'calc(100vh - 280px)',
                maxHeight: '100%',
              }}
            >
              <BrandPlanningTab brandId={brandId} variant="calendarOnly" />
            </Box>
          ) : planningTab === 'dropConfig' ? (
            <Box
              sx={{
                minHeight: 720,
                height: 'calc(100vh - 280px)',
                maxHeight: '100%',
              }}
            >
              <DesignStudioPage mode="dropOnly" />
            </Box>
          ) : planningTab === 'campaigns' && campaigns.length === 0 ? (
            <Alert severity="info">
              No campaigns logged yet. Use “New Campaign” to schedule your next activation.
            </Alert>
          ) : planningTab === 'campaigns' ? (
            <Grid container spacing={2}>
              {campaigns.map((campaign) => (
                <Grid item xs={12} md={6} lg={4} key={campaign._id || campaign.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {campaign.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ledger entry for {campaign.status} campaign
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={(e) => handleCampaignMenuOpen(e, campaign)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      <Chip
                        size="small"
                        label={campaign.status}
                        color={campaign.status === 'active' ? 'success' : campaign.status === 'scheduled' ? 'info' : 'default'}
                        sx={{ mb: 2 }}
                      />

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {campaign.analytics?.reservations?.toLocaleString() || 0} reservations • ${campaign.analytics?.revenue?.toLocaleString() || 0} revenue
                      </Typography>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate(`/campaigns/${campaign._id || campaign.id}`)}
                      >
                        View Campaign
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : null}
        </TabPanel>
        
        {/* Design Tab */}
        <TabPanel value={currentTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Pattern Library (SPIF)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create and customize garment patterns using SPIF (Sivad Pattern Intelligence Framework) for CLO3D integration.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<LaunchIcon />}
                    onClick={() => navigate(`/brands/${brandId}/design/pattern-library`)}
                  >
                    Open Pattern Library
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Pattern Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Manage your pattern catalog - upload new patterns, edit existing ones, and organize pattern blocks for your design workflow.
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<LaunchIcon />}
                    onClick={() => navigate(`/brands/${brandId}/design/patterns`)}
                  >
                    Manage Patterns
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    AI Design Studio
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Generate professional garment design concepts using cloud AI - create visual mockups from text descriptions powered by Ideogram v3 Turbo with excellent text rendering and photorealism.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<LaunchIcon />}
                    onClick={() => navigate(`/brands/${brandId}/design/studio`)}
                  >
                    Open Studio
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Prompt Generator
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Craft high-fidelity creative prompts tailored to the brand&apos;s art direction and campaign concepts.
                  </Typography>
                  <Button
                    variant="outlined"
                    endIcon={<LaunchIcon />}
                    onClick={() => navigate(`/brands/${brandId}/design/prompt-generator`)}
                  >
                    Open Prompt Tool
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Campaigns Tab */}
        <TabPanel value={currentTab} index={3}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={600}>
              Campaigns
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateCampaignDialog(true)}
            >
              New Campaign
            </Button>
          </Box>

          {campaigns.length === 0 ? (
            <Alert severity="info">
              No campaigns created yet. Click "New Campaign" to create your first campaign.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {campaigns.map((campaign) => {
                const launchDate = campaign.timeline?.launchDate ? new Date(campaign.timeline.launchDate) : null;
                return (
                  <Grid item xs={12} md={6} lg={4} key={campaign._id || campaign.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              {campaign.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {launchDate ? `Launched ${formatDistanceToNow(launchDate)} ago` : 'Launch date pending'}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={(e) => handleCampaignMenuOpen(e, campaign)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>

                        <Chip
                          size="small"
                          label={campaign.status}
                          color={campaign.status === 'active' ? 'success' : campaign.status === 'scheduled' ? 'info' : 'default'}
                          sx={{ mb: 2 }}
                        />

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {campaign.analytics?.reservations?.toLocaleString() || 0} reservations • ${campaign.analytics?.revenue?.toLocaleString() || 0} revenue
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            fullWidth
                            onClick={() => navigate(`/campaigns/${campaign._id || campaign.id}`)}
                          >
                            View Details
                          </Button>
                          <Button
                            size="small"
                            fullWidth
                            onClick={() => navigate(`/campaigns/${campaign._id || campaign.id}/edit`)}
                          >
                            Edit
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </TabPanel>

        {/* Storefront Tab */}
        <TabPanel value={currentTab} index={4}>
          <BrandStorefrontTab
            brandId={brandId}
            brandSlug={brand?.slug}
            brandName={brand?.name}
            campaigns={campaigns}
          />
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={currentTab} index={5}>
          <Grid container spacing={3}>
            {/* Revenue chart */}
            <Grid item xs={12} md={8}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Revenue Trends
              </Typography>

              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics?.revenueData || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                      formatter={(value, name) => [
                        name === 'revenue' ? `$${value}` : value,
                        name === 'revenue' ? 'Revenue' : 'Reservations'
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#1a1a1a"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            {/* Campaign breakdown */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Campaign Revenue
              </Typography>

              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics?.campaignBreakdown || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
                    >
                      {analytics?.campaignBreakdown?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={currentTab} index={6}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Brand Settings
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    General Settings
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body1">
                      {brand.settings?.currency || 'USD'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Timezone
                    </Typography>
                    <Typography variant="body1">
                      {brand.settings?.timezone || 'America/New_York'}
                    </Typography>
                  </Box>
                  
                  <Button variant="outlined" startIcon={<SettingsIcon />}>
                    Edit Settings
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>
      
      {/* Campaign action menu */}
      <Menu
        anchorEl={campaignMenuAnchor}
        open={Boolean(campaignMenuAnchor)}
        onClose={handleCampaignMenuClose}
      >
        <MenuItem onClick={() => navigate(`/campaigns/${selectedCampaign?._id}`)}>
          <EditIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => navigate(`/campaigns/${selectedCampaign?._id}/edit`)}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Campaign
        </MenuItem>
        {selectedCampaign?.status === 'scheduled' && (
          <MenuItem onClick={() => handleLaunchCampaign(selectedCampaign._id)}>
            <LaunchIcon sx={{ mr: 1 }} />
            Launch Now
          </MenuItem>
        )}
        {selectedCampaign?.status === 'active' && (
          <MenuItem onClick={() => handlePauseCampaign(selectedCampaign._id)}>
            <PauseIcon sx={{ mr: 1 }} />
            Pause Campaign
          </MenuItem>
        )}
      </Menu>
      
      {/* Create campaign dialog */}
      <Dialog open={createCampaignDialog} onClose={() => setCreateCampaignDialog(false)}>
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Ready to launch a new drop campaign for {brand.name}?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll be able to configure tiers, pricing, timeline, and all campaign details in the campaign builder.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCampaignDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setCreateCampaignDialog(false);
              navigate(`/campaigns/new?brandId=${brandId}`);
            }}
            startIcon={<AddIcon />}
          >
            Start Campaign Builder
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BrandDashboardPage;
